import React, { memo, useCallback, useMemo, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Box, AspectRatio, Image, Text, Flex, Button } from "@chakra-ui/react"

import { useCart } from "@app/hooks/useCart"
import { useGiftWithPurchase } from "@app/hooks/useGiftWithPurchase"
import { useShopify } from "@app/hooks/useShopify"

type Props = {
  product: any
  isDisabled: boolean
}

export const GiftWithPurchaseProduct: React.FC<Props> = memo(({ product, isDisabled }) => {
  const { giftWithPurchase } = useStaticQuery<GatsbyTypes.StaticGWPQuery>(graphql`
    query StaticGWP {
      giftWithPurchase: sanitySettingGwp {
        additionalGift
      }
    }
  `)
  const { loading } = useCart()
  const { addGwp } = useGiftWithPurchase()
  const { formatMoney } = useShopify()

  // Track adding state for THIS product only, so the button label changes to
  // "Adding" just for the product being added — not for every gift card when an
  // unrelated cart action sets the shared `loading` flag.
  const [isAdding, setIsAdding] = useState(false)

  const productImage = useMemo(() => product?.images?.[0], [product?.images])
  const defaultVariant = useMemo(() => product?.variants?.[0], [product?.variants])
  const prices = useMemo(() => {
    const rawAmount = defaultVariant?.priceV2?.amount ?? defaultVariant?.priceV2
    const original = Number(rawAmount)
    const discounted = original * (1 - product?.percentage / 100)
    return { original, discounted }
  }, [defaultVariant?.priceV2, product?.percentage])

  const addToCartHandler = useCallback(async () => {
    if (isDisabled || loading) return
    const variant = defaultVariant
    if (!variant) return
    try {
      setIsAdding(true)
      await addGwp(product, defaultVariant, false, product.percentage)
    } finally {
      setIsAdding(false)
    }
  }, [addGwp, defaultVariant, product, isDisabled, loading])

  return (
    <Flex
      bg="grey.lightest"
      border="1px solid"
      borderColor="primary.softGrey"
      borderRadius="12px"
      padding="8px 12px"
      pos="relative"
      alignItems="center"
      justifyContent="space-between"
    >
      <Flex flex="1" minWidth="0" gap={3} alignItems="flex-start" pr="75px">
        <Box width="56px" flexShrink={0} bg="primary.white" borderRadius="4px">
          <AspectRatio ratio={56 / 71}>
            <Image src={productImage?.originalSrc} alt={productImage?.altText || ""} loading="lazy" />
          </AspectRatio>
        </Box>
        <Flex flexDirection="column" alignItems="flex-start">
          {product?.title && <Text size="md">{product.title}</Text>}
          {typeof product?.percentage === "number" && product.percentage < 100 ? (
            <Flex direction="row" gap={1.5}>
              <Text size="md" color="preset.red">
                {formatMoney(prices.discounted)}
              </Text>
              <Text size="md" textDecoration="line-through" color="secondary.grey">
                {formatMoney(prices.original)}
              </Text>
            </Flex>
          ) : (
            <Text size="md" color="preset.red">
              {giftWithPurchase?.additionalGift}
            </Text>
          )}
        </Flex>
      </Flex>
      <Button
        onClick={addToCartHandler}
        isDisabled={isDisabled || isAdding}
        data-loading={isAdding || undefined}
        cursor={loading || isAdding ? "not-allowed" : "auto"}
        variant="secondary"
        size="small"
        pos="absolute"
        bottom="11px"
        right="11px"
        height="35px"
        width="64px"
        minWidth="64px"
        padding="0 24px"
        borderRadius="40px"
      >
        {isAdding ? "Adding" : `Add`}
      </Button>
    </Flex>
  )
})
